import React, { useEffect, useState, useContext, ChangeEvent } from "react";
import { RouteComponentProps } from "react-router";
import { FormattedMessage } from "react-intl-phraseapp";
import { Alert, Caret, Dropdown } from "@cmsgov/ds-medicare-gov";
import { sortBy } from "sort-by-typescript";
import MedigapPolicyCard from "./MedigapPolicyCard";
import { useTranslate, sendTealiumEvent } from "../../../helpers";
import { AppPage, PrintButton, LoadingMask } from "../../../components";
import routes from "../../../app/routes";
import { AppContext } from "../../../app/store";
import {
  getMedigapPlanByType,
  MedigapPlanType,
  MedigapPlanPolicy,
  MedigapPlan,
} from "../utils/plans";
import { getMedigapPlanPolicies } from "../../../api";

enum MedigapPoliciesSortOption {
  COMPANY_NAME = "company_name",
  RATE_TYPE = "rate_type",
}

const MedigapPoliciesPage: React.FunctionComponent<RouteComponentProps> = ({
  match,
}) => {
  const t = useTranslate();
  const [policies, setPolicies] = useState<MedigapPlanPolicy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [medigapPlan, setMedigapPlan] = useState<MedigapPlan | undefined>(
    undefined
  );
  const {
    state: { county, zipcode = "", language, demographicInfo },
  } = useContext(AppContext);

  const sortPolicies = (
    policiesToSort: MedigapPlanPolicy[],
    sortOn: MedigapPoliciesSortOption
  ): MedigapPlanPolicy[] => {
    if (sortOn === MedigapPoliciesSortOption.COMPANY_NAME) {
      return [...policiesToSort].sort(sortBy("company"));
    } else if (sortOn === MedigapPoliciesSortOption.RATE_TYPE) {
      return [...policiesToSort].sort(sortBy("rate_type", "company"));
    }
    return policiesToSort;
  };

  const fetchMedigapPolicies = async (): Promise<void> => {
    const stateAbbrev = county?.state || "";
    const { medigapPlanType } = match.params as {
      medigapPlanType: MedigapPlanType;
    };
    setMedigapPlan(getMedigapPlanByType(medigapPlanType));

    try {
      setLoading(true);
      const medigapPolicies = await getMedigapPlanPolicies(
        stateAbbrev,
        zipcode,
        medigapPlanType,
        demographicInfo
      );
      setPolicies(
        sortPolicies(medigapPolicies, MedigapPoliciesSortOption.COMPANY_NAME)
      );
      setLoading(false);
      setError("");
    } catch (err) {
      setLoading(false);
      setError(t("medigap.api_error"));
    }
  };

  useEffect(() => {
    sendTealiumEvent("Step 2: Pick your policy", `UI Option: Sort by company`);
  }, []);

  useEffect(() => {
    if (zipcode && county) {
      fetchMedigapPolicies();
    }
  }, [location.hash, zipcode, county]);

  const title = medigapPlan ? t(medigapPlan.plan_policy_name) : "";

  return (
    <AppPage title={title} hideLogin>
      <LoadingMask loading={loading} delay={500} />
      <div className="ds-u-margin-top--2 ds-l-row ds-u-justify-content--between mct-hide-print">
        <a
          href={`#${routes.medigap.plans}?lang=${language}`}
          onClick={(): void => {
            sendTealiumEvent(
              "Step 2: Pick your policy",
              "Back to Medigap plans"
            );
          }}
          className="ds-c-button ds-c-button--transparent PlanDetails__backButton mct-hide-print"
        >
          <Caret left className="ds-u-valign--middle ds-u-margin-right--1" />
          {t("medigap.plan_details.go_back")}
        </a>

        <PrintButton
          tealiumProps={{
            event_action: "Step 2: Pick your policy",
            event_label: "Print",
          }}
        />
      </div>
      <div className="MedigapPlans">
        <div className="ds-u-font-weight--semibold">{t("medigap.step_2")}</div>
        <h1>{title}</h1>
        {error && (
          <Alert variation="error" className="ds-u-margin-top--4">
            {error}
          </Alert>
        )}
        {policies && !loading && (
          <>
            <div className="ds-u-md-display--flex ds-u-justify-content--between ds-u-align-items--center ds-u-margin-y--4">
              <FormattedMessage
                id="medigap.num_policies_in_state"
                values={{ count: policies.length }}
              />
              <Dropdown
                label={t("medigap.plan_results.sort_by")}
                name="sort-policies-by"
                className="ds-u-display--flex ds-u-align-items--center ds-u-margin-top--2 ds-u-md-margin-top--0 mct-hide-print"
                labelClassName="MedigapPlans__sortSelectLabel"
                onChange={(e: ChangeEvent<HTMLSelectElement>): void => {
                  setPolicies(
                    sortPolicies(
                      policies,
                      e.currentTarget.value as MedigapPoliciesSortOption
                    )
                  );
                  sendTealiumEvent(
                    "Step 2: Pick your policy",
                    `UI Option: Sort by ${e.currentTarget.value}`
                  );
                }}
                options={[
                  {
                    label: t("medigap.policies.company_name_sort"),
                    value: MedigapPoliciesSortOption.COMPANY_NAME,
                  },
                  {
                    label: t("medigap.policies.rate_type_sort"),
                    value: MedigapPoliciesSortOption.RATE_TYPE,
                  },
                ]}
              />
            </div>
            {/* the redundant roles are a fix for a safari VO bug */}
            {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
            <ol className="ds-c-list--bare medigap-policies" role="list">
              {policies.map(policy => {
                return (
                  // eslint-disable-next-line jsx-a11y/no-redundant-roles
                  <li key={policy.company} role="listitem">
                    <MedigapPolicyCard policy={policy} />
                  </li>
                );
              })}
            </ol>
          </>
        )}
      </div>
    </AppPage>
  );
};

export default MedigapPoliciesPage;
